export const blogPosts = [
  {
    id: 1,
    slug: "water-treatment-plant-for-kolkata-society",
    title: "How to Choose the Right Water Treatment Plant for Your Kolkata Society",
    date: "May 10, 2026",
    author: "AquaHome Team",
    category: "Water Treatment",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=900&q=80",
    excerpt: "Choosing a water treatment plant for your apartment society in Kolkata involves understanding source water, daily demand and the right technology. Here is a complete practical guide.",
    content: [
      {
        type: "paragraph",
        text: "Residential societies across Kolkata, Newtown and Rajarhat face a common challenge: delivering safe, clean water to hundreds of residents daily from borewell or municipal sources that often carry contamination. Choosing the right water treatment plant is not a one-size-fits-all decision — it depends on your source water quality, the number of flats, peak demand hours and your infrastructure layout.",
      },
      {
        type: "heading",
        text: "Step 1: Get a Source Water Analysis Done",
      },
      {
        type: "paragraph",
        text: "Before selecting any treatment technology, test your water. Borewell water in Kolkata commonly contains high iron, arsenic, hardness minerals and bacterial contamination. Municipal (KMC) water may carry turbidity, residual chlorine and sometimes bacterial load. A lab test will give you TDS, pH, iron, arsenic, hardness, bacterial count and other parameters — this determines which treatment stages you actually need.",
      },
      {
        type: "heading",
        text: "Step 2: Calculate Your Daily Water Demand",
      },
      {
        type: "paragraph",
        text: "A standard estimation uses 135 litres per person per day for drinking and domestic use. Multiply by your total occupancy. Add 10–15% buffer for peak demand. For a 200-flat society with average 3 residents per flat, you need a plant capable of treating approximately 85,000–90,000 litres per day. Getting the capacity wrong in either direction — too small or oversized — creates operational problems.",
      },
      {
        type: "heading",
        text: "Step 3: Match Technology to Water Problems",
      },
      {
        type: "list",
        items: [
          "High iron or manganese → Iron removal plant using aeration and pressure filtration",
          "Arsenic contamination → Arsenic removal plant with activated alumina or iron-based media",
          "Bacterial presence → UV disinfection system or chlorination",
          "High hardness (scaling) → Water softener plant with ion-exchange resin",
          "High TDS or dissolved solids → RO plant for drinking water supply",
          "Turbidity or suspended solids → Multi-media filtration plant",
        ],
      },
      {
        type: "heading",
        text: "Step 4: Plan for IoT Monitoring and Long-Term Maintenance",
      },
      {
        type: "paragraph",
        text: "A good water treatment plant in Kolkata is not a one-time installation — it requires regular monitoring of output quality, filter replacement cycles, resin regeneration and servicing. Modern plants integrate IoT sensors that track TDS, flow rate, pressure and tank levels in real time, giving your facility manager early alerts before problems reach residents. Budget for annual service agreements when selecting your vendor.",
      },
      {
        type: "paragraph",
        text: "AquaHome helps societies in Kolkata, Newtown and Rajarhat through the entire process — from water analysis and plant sizing to installation, commissioning and long-term operational support. Contact us at 8697702088 for a free site assessment.",
      },
    ],
  },
  {
    id: 2,
    slug: "arsenic-contamination-west-bengal-water",
    title: "Arsenic in West Bengal Groundwater: What Kolkata Residents Need to Know",
    date: "May 7, 2026",
    author: "AquaHome Team",
    category: "Arsenic Removal",
    image: "https://images.unsplash.com/photo-1559825481-12a05cc00344?auto=format&fit=crop&w=900&q=80",
    excerpt: "West Bengal has one of the most severe arsenic contamination problems in the world. Here is what Kolkata, Newtown and Rajarhat residents need to understand and what to do about it.",
    content: [
      {
        type: "paragraph",
        text: "West Bengal is among the most arsenic-affected regions in the world. The Ganga delta aquifers — which supply groundwater across Kolkata, Newtown, Rajarhat, North 24 Parganas and South 24 Parganas — carry naturally occurring arsenic leached from sedimentary deposits. Long-term exposure to arsenic above 0.01 mg/L (the WHO safe limit) causes skin lesions, peripheral neuropathy, cardiovascular disease and significantly elevated cancer risk.",
      },
      {
        type: "heading",
        text: "Which Areas of Kolkata Are Most Affected?",
      },
      {
        type: "paragraph",
        text: "Arsenic is not uniformly distributed. Districts with high contamination include North 24 Parganas, South 24 Parganas, Murshidabad and Nadia. Within the Kolkata metropolitan area, borewell-dependent properties — particularly those drawing from deeper aquifers — are at higher risk. Properties in Newtown and Rajarhat that rely on borewell water should treat arsenic as a real and testable concern, not just a rural problem.",
      },
      {
        type: "heading",
        text: "How Arsenic Removal Plants Work",
      },
      {
        type: "list",
        items: [
          "Activated Alumina Filtration: Water passes through activated alumina media that adsorbs arsenate ions — highly effective for arsenic V (As5+)",
          "Iron-Based Media (GFH, CFH): Granular ferric hydroxide adsorbs both arsenic III and arsenic V — effective across a wider range",
          "Coagulation and Flocculation: Ferric sulphate or alum is dosed to precipitate arsenic — used in larger community plants",
          "RO Membranes: Reverse osmosis rejects over 95% of arsenic — effective but generates reject water",
        ],
      },
      {
        type: "heading",
        text: "What Arsenic Level Is Safe for Drinking Water?",
      },
      {
        type: "paragraph",
        text: "The Bureau of Indian Standards (BIS) has set a permissible limit of 0.01 mg/L for arsenic in drinking water, aligned with the WHO guideline. If your borewell water tests above this level, treatment is not optional — it is a health necessity. An arsenic removal plant designed for your daily demand and inlet arsenic concentration can consistently bring output below 0.005 mg/L, well within safe limits.",
      },
      {
        type: "paragraph",
        text: "AquaHome installs arsenic removal plants for residential societies, villas and housing developments across Kolkata, Newtown and Rajarhat. We begin with a free water test and recommend only what your specific water condition requires. Call 8697702088 to schedule a test.",
      },
    ],
  },
  {
    id: 3,
    slug: "ro-plant-vs-uv-system-vs-softener-kolkata",
    title: "RO Plant vs UV System vs Softener Plant: Which Does Your Kolkata Home Need?",
    date: "May 4, 2026",
    author: "AquaHome Team",
    category: "Water Purification",
    image: "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?auto=format&fit=crop&w=900&q=80",
    excerpt: "RO plants, UV systems and softener plants each solve different water problems. Here is a clear comparison to help you choose the right system for your home or society in Kolkata.",
    content: [
      {
        type: "paragraph",
        text: "Residents and facility managers in Kolkata, Newtown and Rajarhat often ask: should I install an RO plant, a UV system or a water softener? The answer depends entirely on what is wrong with your water — these three technologies solve very different problems and are often used together rather than as alternatives.",
      },
      {
        type: "heading",
        text: "RO Plant (Reverse Osmosis)",
      },
      {
        type: "paragraph",
        text: "An RO plant forces water through a semi-permeable membrane that rejects dissolved solids, heavy metals (including arsenic and lead), nitrates and most ionic contaminants. It significantly reduces TDS (Total Dissolved Solids). Best suited for: high TDS water, water with dissolved arsenic or heavy metals, borewell water with chemical contamination. RO plants produce reject water (typically 30–40% of input) and require pre-filtration to protect the membrane. For societies, a centralised RO plant with a dedicated drinking water line is the most efficient approach.",
      },
      {
        type: "heading",
        text: "UV System (Ultraviolet Disinfection)",
      },
      {
        type: "paragraph",
        text: "A UV system uses ultraviolet light at 254 nm wavelength to destroy the DNA of bacteria, viruses and cysts — rendering them unable to reproduce. It does not remove dissolved chemicals or reduce TDS. Best suited for: water that is microbiologically contaminated but has acceptable chemical parameters. UV systems are chemical-free, have no reject water and are highly effective when the inlet water is clear. For Kolkata societies on municipal supply with acceptable TDS but bacterial concern, UV is often the right choice — sometimes combined with a pre-filter.",
      },
      {
        type: "heading",
        text: "Water Softener Plant (Ion Exchange)",
      },
      {
        type: "paragraph",
        text: "A softener plant uses ion-exchange resin to replace calcium and magnesium ions (which cause hardness) with sodium ions. It does not remove bacteria, arsenic or dissolved chemicals — only hardness. Best suited for: scaling problems in geysers, pipes, washing machines and bathroom fixtures. Borewell water in Newtown and Rajarhat often has high hardness (above 300 mg/L as CaCO3), causing limescale deposits and reducing the life of appliances. A softener plant solves this specific problem.",
      },
      {
        type: "heading",
        text: "What Most Kolkata Societies Actually Need",
      },
      {
        type: "list",
        items: [
          "Borewell with high iron + bacteria → Iron removal plant + UV system",
          "Borewell with arsenic + high TDS → Arsenic removal plant + RO plant",
          "Borewell with high hardness + bacteria → Softener plant + UV system",
          "Municipal supply with bacterial concern → UV system + sediment filter",
          "Borewell with multiple issues → Multi-stage plant combining all relevant technologies",
        ],
      },
      {
        type: "paragraph",
        text: "AquaHome conducts a source water analysis before recommending any system. We do not sell a single solution for all properties — we design what your water actually needs. Call 8697702088 for a free water test and consultation.",
      },
    ],
  },
  {
    id: 4,
    slug: "iron-removal-plant-borewell-water-kolkata",
    title: "Iron in Borewell Water: Complete Guide to Iron Removal Plants in Kolkata",
    date: "April 30, 2026",
    author: "AquaHome Team",
    category: "Iron Removal",
    image: "https://images.unsplash.com/photo-1584432810601-6c7f27d2362b?auto=format&fit=crop&w=900&q=80",
    excerpt: "High iron in borewell water is one of the most common problems for Kolkata, Newtown and Rajarhat residential societies. Here is how iron removal plants work and why your property may need one.",
    content: [
      {
        type: "paragraph",
        text: "Iron is the most frequently detected borewell water problem across Kolkata, Newtown and Rajarhat. The Ganga delta geology releases dissolved iron into groundwater naturally — and many borewell sources in this region measure iron concentrations between 1 and 10 mg/L, far above the BIS permissible limit of 0.3 mg/L for drinking water. If left untreated, high iron causes rust-coloured water, metallic taste, orange staining on tiles and sanitary ware, and accelerated corrosion of plumbing and geysers.",
      },
      {
        type: "heading",
        text: "Signs Your Society's Borewell Water Has High Iron",
      },
      {
        type: "list",
        items: [
          "Reddish-brown or orange tinge in tap water, especially after the pump starts",
          "Rust stains on bathroom tiles, toilet bowls and washbasins",
          "Metallic or bitter taste in drinking water",
          "Clogged showerheads and tap aerators with orange deposits",
          "Black or brown slime inside toilet tanks (iron bacteria growth)",
          "Yellowing of white laundry despite using detergent",
        ],
      },
      {
        type: "heading",
        text: "How Iron Removal Plants Work",
      },
      {
        type: "paragraph",
        text: "Most iron removal plants use a two-stage process: oxidation followed by filtration. In the oxidation stage, dissolved ferrous iron (Fe2+) is converted to insoluble ferric iron (Fe3+) by introducing air (aeration) or a chemical oxidant such as potassium permanganate or chlorine. In the filtration stage, the oxidised iron particles are trapped in a media bed — typically manganese greensand, birm or anthracite — and removed from the water stream. The filter media is backwashed periodically to remove accumulated iron and restore capacity.",
      },
      {
        type: "heading",
        text: "Iron Removal Plant Sizing for Residential Societies",
      },
      {
        type: "paragraph",
        text: "Plant capacity must match your peak flow rate, not just daily volume. A 200-flat society may need a plant rated at 10,000 to 15,000 litres per hour to handle morning peak demand without pressure drops. The inlet iron concentration also affects media selection and bed depth — higher iron (above 5 mg/L) may require a two-pass system or chemical dosing ahead of filtration. AquaHome conducts a site assessment and water test before recommending capacity.",
      },
      {
        type: "heading",
        text: "Iron and Manganese: Often Found Together",
      },
      {
        type: "paragraph",
        text: "Borewell water with high iron frequently also contains elevated manganese, which causes black staining and has its own health limits (0.1 mg/L as per BIS). Iron removal plants can be designed to remove both simultaneously using appropriate oxidation and filtration media. If your water test shows both, a combined iron and manganese removal plant is more cost-effective than two separate systems.",
      },
      {
        type: "paragraph",
        text: "AquaHome designs and installs iron removal plants for residential societies, villas and real estate projects across Kolkata, Newtown and Rajarhat. Call 8697702088 for a free borewell water test.",
      },
    ],
  },
  {
    id: 5,
    slug: "softener-plant-hard-water-newtown-rajarhat",
    title: "Hard Water Problems in Newtown & Rajarhat: Softener Plant Installation Guide",
    date: "April 26, 2026",
    author: "AquaHome Team",
    category: "Softener Plant",
    image: "https://images.unsplash.com/photo-1511818966892-d7d671e672a2?auto=format&fit=crop&w=900&q=80",
    excerpt: "Hard water is widespread in Newtown and Rajarhat borewell sources. A water softener plant protects your plumbing, geysers and appliances. Here is everything you need to know.",
    content: [
      {
        type: "paragraph",
        text: "Many residential societies and villas in Newtown and Rajarhat draw borewell water with high total hardness — often measuring between 250 and 500 mg/L as CaCO3. Hard water does not cause immediate health problems, but it creates significant infrastructure damage over time: limescale buildup inside geysers and boilers, clogged pipes, damaged washing machine heating elements, spotted glassware and dry skin after bathing. A water softener plant is the most direct and effective solution.",
      },
      {
        type: "heading",
        text: "What Makes Water Hard?",
      },
      {
        type: "paragraph",
        text: "Hardness is caused by dissolved calcium (Ca2+) and magnesium (Mg2+) ions in groundwater. As water percolates through limestone and dolomite rock formations — common in Bengal's geological strata — it dissolves these minerals. The resulting hard water forms scale deposits when heated, which is why geysers and solar water heaters in Newtown and Rajarhat often fail prematurely or lose efficiency within 2–3 years without a softener.",
      },
      {
        type: "heading",
        text: "How a Water Softener Plant Works",
      },
      {
        type: "paragraph",
        text: "A softener plant uses ion-exchange resin beads loaded with sodium ions. As hard water passes through the resin bed, calcium and magnesium ions attach to the resin and sodium ions are released in exchange — the output is soft water with no hardness. When the resin is exhausted (fully loaded with calcium and magnesium), it is regenerated by flushing with a sodium chloride (common salt) brine solution, restoring its exchange capacity. For residential societies, fully automatic softener plants with timed or demand-based regeneration eliminate manual intervention.",
      },
      {
        type: "heading",
        text: "Softener Plant Benefits for Newtown & Rajarhat Societies",
      },
      {
        type: "list",
        items: [
          "Extends geyser, boiler and solar heater life by 3–5 years by preventing scale",
          "Reduces soap and detergent consumption by 30–40% (soft water lathers more easily)",
          "Eliminates white scale deposits on tiles, mirrors, taps and shower screens",
          "Protects piping networks from internal scaling and flow restriction",
          "Improves skin and hair feel after bathing",
          "Reduces maintenance costs for washing machines and dishwashers",
        ],
      },
      {
        type: "heading",
        text: "Softener Plant vs RO Plant: A Common Confusion",
      },
      {
        type: "paragraph",
        text: "A softener plant reduces hardness but does not remove bacteria, arsenic, iron or reduce TDS significantly. An RO plant reduces TDS and removes dissolved contaminants but does not soften water efficiently. For a society with both hard water and high TDS, the right design is a softener plant ahead of the RO plant — the softener protects the RO membrane from scaling and extends its life significantly.",
      },
      {
        type: "paragraph",
        text: "AquaHome installs water softener plants for residential societies, luxury villas and real estate projects across Newtown, Rajarhat and greater Kolkata. Call 8697702088 for a free hardness test and plant sizing consultation.",
      },
    ],
  },
  {
    id: 6,
    slug: "water-filter-installation-guide-kolkata-apartments",
    title: "Water Filter Installation Guide for Apartment Societies in Kolkata",
    date: "April 22, 2026",
    author: "AquaHome Team",
    category: "Water Filter",
    image: "https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?auto=format&fit=crop&w=900&q=80",
    excerpt: "Installing a water filter system for your Kolkata apartment society requires choosing the right filter type, sizing it correctly and planning the installation around your utility layout.",
    content: [
      {
        type: "paragraph",
        text: "Water filter installation for a residential apartment society in Kolkata is more complex than installing a single household unit — it involves selecting a centralised system that handles the full daily demand of all flats, integrating it with the existing utility layout and ensuring consistent water quality at every tap throughout the building.",
      },
      {
        type: "heading",
        text: "Types of Water Filters for Kolkata Apartments",
      },
      {
        type: "list",
        items: [
          "Sediment Filters: Remove suspended particles, sand and turbidity — used as a first stage before other treatment",
          "Activated Carbon Filters: Remove chlorine, chloramines and organic compounds — improves taste and odour",
          "Multi-Media Filters: Layered sand, anthracite and gravel beds — handles high turbidity borewell water",
          "RO Membrane Filters: Remove dissolved solids, heavy metals and arsenic — used for drinking water quality",
          "UV Filters: Ultraviolet disinfection — destroys bacteria and viruses without chemicals",
          "Iron Removal Filters: Oxidation and filtration media specifically for iron and manganese",
        ],
      },
      {
        type: "heading",
        text: "Centralised vs Point-of-Use Filters for Societies",
      },
      {
        type: "paragraph",
        text: "A centralised water filter system treats the entire water supply at the source — the borewell pump room or overhead tank inlet — before distribution. Every flat receives pre-treated water. This is more economical for large societies (50+ flats) than equipping each flat with individual units, and it ensures consistent quality building-wide. Point-of-use filters (under-sink or countertop) are suitable as a final polishing stage for drinking water in individual flats.",
      },
      {
        type: "heading",
        text: "Key Sizing Parameters for Society Water Filter Systems",
      },
      {
        type: "paragraph",
        text: "Correct sizing prevents the most common failure in society water filter installations: undersizing that leads to pressure drops during peak hours. Key parameters are: (1) Peak flow rate in litres per hour — typically 50–60 litres per person per hour during morning peak; (2) Inlet water quality parameters from a lab test; (3) Available space in the pump room for filter vessels and associated equipment; (4) Overhead tank capacity and refill cycle frequency.",
      },
      {
        type: "heading",
        text: "Maintenance Schedule for Society Water Filters",
      },
      {
        type: "list",
        items: [
          "Sediment pre-filter cartridge: Replace every 3–6 months depending on turbidity",
          "Activated carbon filter: Replace or regenerate every 6–12 months",
          "RO membrane: Replace every 2–3 years with proper pre-filtration",
          "UV lamp: Replace annually regardless of apparent performance",
          "Iron removal media: Backwash weekly, replace media every 3–5 years",
          "Softener resin: Regenerate weekly with salt brine; resin lasts 8–10 years",
        ],
      },
      {
        type: "paragraph",
        text: "AquaHome handles complete water filter installation for apartment societies, villas and real estate projects across Kolkata, Newtown and Rajarhat — from water testing and system design to installation, commissioning and annual service. Call 8697702088 to get started.",
      },
    ],
  },
];
